import { Outlet } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useEffect, useState, useCallback } from 'react';
import { api, setTokenGetter } from '../lib/api';
import { Sidebar } from '../components/Sidebar';
import { NotificationBell } from '../components/NotificationBell';
import { Toast } from '../components/Toast';
import { useRealtimeNotifications } from '../hooks/useRealtimeNotifications';
import { useNotificationPolling } from '../hooks/useNotificationPolling';
import { useNotificationStore } from '../stores/useNotificationStore';
import { useAuthStore } from '../stores/useAuthStore';
import type { UserRecord, SubscriptionTier } from '../types';
import { T } from '../lib/design-tokens';

/** Response from GET /user/me */
interface UserMeResponse {
  id: string;
  email: string;
  phoneVerified: boolean;
  gmailConnected: boolean;
  subscriptionTier: SubscriptionTier;
  callsUsedThisMonth: number;
  callsLimit: number;
  emailsUsedThisMonth: number;
  emailsLimit: number;
  usagePeriodStart: string;
  createdAt: string;
}

export function AppLayout() {
  const { getToken } = useAuth();
  const [toast, setToast] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const setUser = useAuthStore((s) => s.setUser);
  const setUsage = useAuthStore((s) => s.setUsage);

  useEffect(() => {
    setTokenGetter(getToken);
  }, [getToken]);

  useEffect(() => {
    api<UserMeResponse>('/user/me')
      .then((data) => {
        const user: UserRecord = {
          id: data.id,
          clerkId: '',
          email: data.email,
          phoneNumber: null,
          phoneVerified: data.phoneVerified,
          gmailConnected: data.gmailConnected,
          gmailRefreshToken: null,
          subscriptionTier: data.subscriptionTier,
          callsUsedThisMonth: data.callsUsedThisMonth,
          callsLimit: data.callsLimit,
          emailsUsedThisMonth: data.emailsUsedThisMonth,
          emailsLimit: data.emailsLimit,
          usagePeriodStart: data.usagePeriodStart,
          createdAt: data.createdAt,
          updatedAt: data.createdAt,
        };
        setUser(user);
        setUsage({
          voice: {
            allowed: data.callsLimit > 0,
            used: data.callsUsedThisMonth,
            limit: data.callsLimit,
            tier: data.subscriptionTier,
          },
          email: {
            allowed: data.emailsLimit > 0,
            used: data.emailsUsedThisMonth,
            limit: data.emailsLimit,
            tier: data.subscriptionTier,
          },
        });
      })
      .catch(() => {
        setUser(null);
        setUsage(null);
      });
  }, [setUser, setUsage]);

  useRealtimeNotifications();
  useNotificationPolling();

  const notifications = useNotificationStore((s) => s.notifications);
  const lastNotifId = notifications[0]?.id;

  const handleNewNotification = useCallback(() => {
    const latest = notifications[0];
    if (!latest || latest.read) return;

    const title = latest.title.toLowerCase();
    if (title.includes('completed')) {
      setToast(`Task completed: ${latest.message}`);
    } else if (title.includes('failed')) {
      setToast(`Task failed: ${latest.message}`);
    } else if (title.includes('executing')) {
      setToast('Task is now executing...');
    } else if (title.includes('waiting')) {
      setToast('Email sent, waiting for reply...');
    } else if (title.includes('no response')) {
      setToast('No response received for task.');
    } else if (latest.type === 'usage_warning') {
      setToast(latest.message);
    }
  }, [notifications]);

  useEffect(() => {
    if (lastNotifId) {
      handleNewNotification();
    }
  }, [lastNotifId, handleNewNotification]);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: T.bg }}>
      {/* Desktop sidebar */}
      <div className="sidebar-desktop">
        <Sidebar />
      </div>

      {/* Mobile hamburger */}
      <button
        className="sidebar-mobile-toggle fixed top-4 left-4 z-[60] border-none rounded-[10px] cursor-pointer flex items-center justify-center"
        style={{
          width: 40,
          height: 40,
          background: T.surface,
          border: `1px solid ${T.glassBorder}`,
          color: T.cream,
        }}
        onClick={() => setMobileMenuOpen(true)}
      >
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {mobileMenuOpen && (
        <div className="sidebar-mobile-overlay fixed inset-0 z-[55]">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="relative z-10 h-full" onClick={() => setMobileMenuOpen(false)}>
            <Sidebar />
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto relative">
        <div className="absolute top-5 right-6 z-50">
          <NotificationBell />
        </div>
        <Outlet />
      </div>

      {/* Grain overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 9999,
          opacity: 0.035,
          mixBlendMode: 'overlay',
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Top edge glow */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${T.goldDim}, transparent)`,
          zIndex: 100,
          pointerEvents: 'none',
        }}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
